import { EditIcon } from "../../../../../assets/icons"
import { TrashCanIcon } from "../../../../../assets/customermenu-icons"
import { useDeleteUserTeamMemberMutation, useLazyGetAllTeamMembersForCustomerQuery } from "../../../../../service/team-management-service"
import { UserTeamMemberResponse } from "../../../../../service/contract/team-management"
import CustomerPageLayout from "../../../customer-page-layout/CustomerPageLayout"
import { ConfirmationModal } from "../../../components/confirmation-modal/confirmation-modal"
import styles from "./manage-team-members-page.module.scss"
import { useCallback, useEffect, useState } from "react"
import { CustomerLoader } from "../../../components"
import { toast } from "react-toastify"
import { useTranslation } from "react-i18next"
import { ManageMemberPermissions } from "./components/manage-member-permissions/manage-member-permissions"
import { useSelector } from "react-redux"
import { RootState } from "../../../../../stores/store"
import { CustomerType } from "../../../../../model/customer/customer-type"

export const ManageTeamMembersPage = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: RootState) => state.user);

  const [getAllTeamMembersForCustomer, { isLoading, isFetching }] = useLazyGetAllTeamMembersForCustomerQuery();
  const [deleteTeamMember, { isLoading: isDeleting }] = useDeleteUserTeamMemberMutation();

  const [teamMembers, setTeamMembers] = useState<UserTeamMemberResponse[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<UserTeamMemberResponse | null>(null);

  const [isEditMember, setIsEditMember] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<UserTeamMemberResponse | null>(null);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const result = await getAllTeamMembersForCustomer().unwrap();
      setTeamMembers(result);
    } catch (error: any) {
      // toast.error(error?.data?.title || "Failed to fetch team members.");
    }
  }, [getAllTeamMembersForCustomer]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);
  
  const handleEditMember = (member: UserTeamMemberResponse) => {
    setSelectedMember(member);
    setIsEditMember(true);
  };

  const handleDeleteMember = (member: UserTeamMemberResponse) => {
    setMemberToDelete(member);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setMemberToDelete(null);
  };

  const handleConfirmDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      await deleteTeamMember({ userReferenceId: memberToDelete.id }).unwrap();
      setTeamMembers((prev) => prev.filter((m) => m.id !== memberToDelete.id));
      handleCloseDeleteModal();
      toast.success(t("account.web.teamMemberRemovedSuccessfully"));
    } catch (error: any) {
      toast.error(error?.data?.title || t("account.web.failedToRemoveTeamMember"));
    }
  };

  const handleCancelEditMember = () => {
    setIsEditMember(false);
    setSelectedMember(null);
  }

  const handleApplyEditMember = () => {
    fetchTeamMembers();
    setIsEditMember(false);
    setSelectedMember(null);
  }

  if (isLoading || isFetching) {
    return (
      <CustomerPageLayout title={userInfo.customerType === CustomerType.Individual ? t("common.labels.familyAccountManagement") : t("account.corporateAccountManagement")}>
        <CustomerLoader
          title={t("account.web.loadingTeamMembers")}
          subtitle={t("account.web.pleaseWaitFetchingTeamMembers")}
        />
      </CustomerPageLayout>
    );
  }

  if (isEditMember) {
    return <ManageMemberPermissions selectedMember={selectedMember} onCancel={handleCancelEditMember} onContinue={handleApplyEditMember} />;
  }

  return (
    <CustomerPageLayout title={userInfo.customerType === CustomerType.Individual ? t("common.labels.familyAccountManagement") : t("account.corporateAccountManagement")}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>{t("corporate.manageMemberPermissions")}</h2>
          <p className={styles.subtitle}>
            {teamMembers.length === 0
              ? (userInfo.customerType === CustomerType.Individual ? t("account.web.noMembersInFamilyAccount") : t("account.web.noMembersInCorporateAccount"))
              : t("corporate.adjustRoles")}
          </p>
        </div>
        <div className={styles.membersList}>
          {teamMembers.map((member) => {
            const fullName = `${member.firstName} ${member.lastName}`.trim();

            return (
              <div key={member.id} className={styles.memberCard}>
                <div className={styles.memberLeft}>
                  <div className={styles.avatar}>
                    {member.image ? (
                      <img src={member.image} alt={fullName} />
                    ) : (
                      <span>{member.firstName.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>{fullName}</span>
                    <span className={styles.memberEmail}>{member.email}</span>
                    <span className={styles.roleBadge}>{member.role.name}</span>
                  </div>
                </div>
                <div className={styles.memberActions}>
                  <button
                    className={`${styles.actionButton} ${styles.editButton}`}
                    onClick={() => handleEditMember(member)}
                    aria-label={t("account.web.editMemberAria", { name: fullName })}
                  >
                    <EditIcon width={18} height={18} />
                  </button>
                  <button
                    className={styles.actionButton}
                    onClick={() => handleDeleteMember(member)}
                    aria-label={t("account.web.deleteMemberAria", { name: fullName })}
                  >
                    <TrashCanIcon width={18} height={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDeleteMember}
          title={t("account.web.removeTeamMember")}
          message={t(userInfo.customerType === CustomerType.Individual ? "account.web.confirmRemoveMemberFamily" : "account.web.confirmRemoveMemberCorporate", { name: memberToDelete ? `${memberToDelete.firstName} ${memberToDelete.lastName}` : t("account.web.thisMember") })}
          confirmText={t("account.web.remove")}
          loadingText={t("account.web.removing")}
          isLoading={isDeleting}
          variant="danger"
        />
      </div>
    </CustomerPageLayout>
  )
}
